import { createContext, useContext, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { updateCaseStudy, addCaseStudy, getCaseStudyBySlug } from "../../api/case-studies";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { useSearchParams, useNavigate } from "react-router-dom";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const toaster = useToaster();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();

  const getCaseStudyQuery = useQuery({
    queryKey: ["case-study", id],
    queryFn: () => getCaseStudyBySlug(id),
    enabled: !!id,
  });

  const updateCaseStudyQuery = useMutation({ mutationFn: updateCaseStudy });
  const addCaseStudyQuery = useMutation({ mutationFn: addCaseStudy });

  const initialValues = useMemo(() => {
    const caseData = getCaseStudyQuery?.data?.data || {};
    return {
      caseStudy: {
        title: caseData?.title || "",
        slug_url: caseData?.slug_url || "",
        date: caseData?.date || "",
        client: caseData?.client || "",
        category: caseData?.category || "",
        author: caseData?.author || "admin",
        overview: caseData?.overview || "",
        challenge: caseData?.challenge || "",
        solution: caseData?.solution || "",
        results: caseData?.results?.length ? caseData.results : [""],
        metrics: caseData?.metrics?.length ? caseData.metrics : [{ label: "", value: "" }],
        keywords: caseData?.keywords?.join(", ") || "",
        banner_img: caseData?.banner_img || "",
        is_publish: caseData?.is_publish || false,
        meta_title: caseData?.meta_title || "",
        meta_description: caseData?.meta_description || "",
        meta_keywords: caseData?.meta_keywords || "",
        meta_slug_url: caseData?.meta_slug_url || ""
      },
    };
  }, [getCaseStudyQuery]);

  const handleSubmit = async (values) => {
    const payload = {
      title: values?.caseStudy?.title || "",
      slug_url: values?.caseStudy?.slug_url || "",
      date: values?.caseStudy?.date || "",
      client: values?.caseStudy?.client || "",
      category: values?.caseStudy?.category || "",
      author: values?.caseStudy?.author || "admin",
      overview: values?.caseStudy?.overview || "",
      challenge: values?.caseStudy?.challenge || "",
      solution: values?.caseStudy?.solution || "",
      results: (values?.caseStudy?.results || []).filter((r) => r && r.trim()),
      metrics: (values?.caseStudy?.metrics || []).filter((m) => m.label || m.value),
      keywords: values?.caseStudy?.keywords
        ? values.caseStudy.keywords.split(",").map((k) => k.trim()).filter(Boolean)
        : [],
      banner_img: values?.caseStudy?.banner_img || "",
      is_publish: values?.caseStudy?.is_publish || false,
      meta_title: values?.caseStudy?.meta_title || "",
      meta_description: values?.caseStudy?.meta_description || "",
      meta_keywords: values?.caseStudy?.meta_keywords || "",
      meta_slug_url: values?.caseStudy?.meta_slug_url || ""
    };

    if (id && getCaseStudyQuery?.data?.data?._id) {
      payload.id = getCaseStudyQuery.data.data._id;
      updateCaseStudyQuery.mutate(payload, {
        onSuccess: () => {
          toaster.success(
            Const.MESSAGES.SUCCESS,
            Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
          );
          navigate("/case-studies");
        },
        onError: (err) => {
          toaster.error(Const.MESSAGES.FAILED, err?.response?.data?.error || "Update failed");
        }
      });
      return;
    }

    addCaseStudyQuery.mutate(payload, {
      onSuccess: () => {
        toaster.success(
          Const.MESSAGES.SUCCESS,
          Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
        );
        navigate("/case-studies");
      },
      onError: (err) => {
        toaster.error(Const.MESSAGES.FAILED, err?.response?.data?.error || "Save failed");
      }
    });
  };

  return (
    <PageContext.Provider
      value={{
        initialValues,
        getCaseStudyQuery,
        handleSubmit,
        isEditing: !!id,
      }}>
      {children}
    </PageContext.Provider>
  );
};

export const useAddCaseStudyProvider = () => {
  return useContext(PageContext);
};
